import React, { useState } from 'react'
import { Button, StatusPill, FilePicker } from '../../components/Common'
import { formatFileSize } from '../../components/Common/fileRowHelpers'
import { useNls } from '../../nls/nlsProvider'
import {
  verifyEvidenceManifestWebCrypto,
  buildVerificationKeyEndpoint,
  validateManifestFiles,
  generateSha256SumsText,
  FIXED_SHASUM_COMMAND,
} from './verifyEvidenceHelpers'
import './verifyEvidence.css'

export interface ManifestFileEntry {
  part: number
  path: string
  bytes: number
  sha256: string
}

export interface ManifestData {
  exportId?: string
  workspaceId?: string
  workspaceName?: string
  keyVersion?: string
  generatedAt?: string
  scope?: {
    from?: string
    to?: string
    sectors?: string[]
  }
  files?: ManifestFileEntry[]
}

export const VerifyEvidencePage: React.FC = () => {
  const { t } = useNls()
  const [manifestFile, setManifestFile] = useState<File | null>(null)
  const [signatureFile, setSignatureFile] = useState<File | null>(null)
  const [manifestData, setManifestData] = useState<ManifestData | null>(null)
  const [verifying, setVerifying] = useState(false)
  const [verifyStatus, setVerifyStatus] = useState<'IDLE' | 'VALID' | 'INVALID'>('IDLE')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const handleManifestSelected = (file: File) => {
    setManifestFile(file)
    setVerifyStatus('IDLE')
    setErrorMessage(null)

    const reader = new FileReader()
    reader.onload = () => {
      try {
        const text = reader.result as string
        const parsed = JSON.parse(text)
        setManifestData(parsed)
      } catch {
        setErrorMessage(t('NLSVerifyEvidenceInvalidJson'))
        setVerifyStatus('INVALID')
      }
    }
    reader.readAsText(file)
  }

  const handleSignatureSelected = (file: File) => {
    setSignatureFile(file)
    setVerifyStatus('IDLE')
    setErrorMessage(null)
  }

  const handleVerify = async () => {
    if (!manifestFile || !signatureFile) {
      setErrorMessage(t('NLSVerifyEvidenceMissingFiles'))
      setVerifyStatus('INVALID')
      return
    }
    setVerifying(true)
    setErrorMessage(null)
    setVerifyStatus('IDLE')

    try {
      const manifestBuffer = await manifestFile.arrayBuffer()
      const manifestBytes = new Uint8Array(manifestBuffer)

      const sigText = (await signatureFile.text()).trim()

      const parsed: ManifestData = JSON.parse(new TextDecoder().decode(manifestBytes))
      setManifestData(parsed)

      const keyVersion = parsed.keyVersion
      if (!keyVersion) {
        setErrorMessage(t('NLSVerifyEvidenceMissingKeyVersion'))
        setVerifyStatus('INVALID')
        return
      }

      // Validate manifest file entries for path traversal, newlines, sha256 formatting
      const fileValidation = validateManifestFiles(parsed.files || [])
      if (!fileValidation.valid) {
        setErrorMessage(t('NLSVerifyEvidenceInvalidManifestFiles'))
        setVerifyStatus('INVALID')
        return
      }

      // Fetch public key PEM
      const endpoint = buildVerificationKeyEndpoint(keyVersion)
      const res = await fetch(endpoint)
      if (!res.ok) {
        setErrorMessage(t('NLSVerifyEvidenceKeyFetchFailed'))
        setVerifyStatus('INVALID')
        return
      }
      const pem = await res.text()

      // WebCrypto verification
      const isValid = await verifyEvidenceManifestWebCrypto(manifestBytes, sigText, pem)
      setVerifyStatus(isValid ? 'VALID' : 'INVALID')
      if (!isValid) {
        setErrorMessage(t('NLSVerifyEvidenceSignatureMismatch'))
      }
    } catch {
      setErrorMessage(t('NLSVerifyEvidenceFailedGeneric', { reason: 'verification-error' }))
      setVerifyStatus('INVALID')
    } finally {
      setVerifying(false)
    }
  }

  const handleDownloadSums = () => {
    if (!manifestData?.files) return
    const text = generateSha256SumsText(manifestData.files)
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'SHA256SUMS'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleCopyFixedCommand = () => {
    navigator.clipboard?.writeText(FIXED_SHASUM_COMMAND)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="verifyEvidencePage" data-testid="verify-evidence-page">
      <header className="verifyEvidencePage__header">
        <h1>{t('NLSVerifyEvidenceTitle')}</h1>
        <p className="verifyEvidencePage__subtitle">{t('NLSVerifyEvidenceSubtitle')}</p>
      </header>

      <main className="verifyEvidencePage__content">
        <section className="card verifyEvidenceCard">
          <h2>{t('NLSVerifyEvidenceSelectFilesPrompt')}</h2>
          <div className="verifyEvidencePickers">
            <div className="field">
              <label htmlFor="verify-manifest-input" className="field__label">
                {t('NLSVerifyEvidencePickManifest')}
              </label>
              <FilePicker
                id="verify-manifest-input"
                dataTestId="verify-manifest-input"
                accept=".json,application/json"
                maxBytes={50 * 1024 * 1024}
                multiple={false}
                onFiles={(files) => {
                  if (files[0]) handleManifestSelected(files[0])
                }}
                hint={manifestFile ? manifestFile.name : undefined}
              />
            </div>

            <div className="field">
              <label htmlFor="verify-sig-input" className="field__label">
                {t('NLSVerifyEvidencePickSignature')}
              </label>
              <FilePicker
                id="verify-sig-input"
                dataTestId="verify-sig-input"
                accept=".sig,text/plain"
                maxBytes={10 * 1024 * 1024}
                multiple={false}
                onFiles={(files) => {
                  if (files[0]) handleSignatureSelected(files[0])
                }}
                hint={signatureFile ? signatureFile.name : undefined}
              />
            </div>
          </div>

          <div className="verifyEvidenceActions">
            <Button
              variant="primary"
              onClick={handleVerify}
              disabled={!manifestFile || !signatureFile || verifying}
              loading={verifying}
            >
              {verifying ? t('NLSVerifyEvidenceVerifying') : t('NLSVerifyEvidenceVerifyButton')}
            </Button>
          </div>

          {errorMessage && (
            <div className="evidenceErrorText" role="alert" data-testid="verify-error">
              {errorMessage}
            </div>
          )}

          {verifyStatus !== 'IDLE' && (
            <div className="verifyResultBanner" data-testid="verify-result">
              {verifyStatus === 'VALID' ? (
                <StatusPill tone="success">{t('NLSVerifyEvidenceValid')}</StatusPill>
              ) : (
                <StatusPill tone="danger">{t('NLSVerifyEvidenceInvalid')}</StatusPill>
              )}
            </div>
          )}
        </section>

        {verifyStatus === 'VALID' && manifestData && (
          <section className="card verifyEvidenceMetadata" data-testid="verify-metadata">
            <h2>{t('NLSVerifyEvidenceManifestDetails')}</h2>
            <dl className="verifyMetaGrid">
              <div>
                <dt>{t('NLSVerifyEvidenceWorkspace')}</dt>
                <dd>
                  <strong>{manifestData.workspaceName || manifestData.workspaceId || '—'}</strong>
                </dd>
              </div>
              <div>
                <dt>{t('NLSVerifyEvidencePeriod')}</dt>
                <dd>
                  {manifestData.scope?.from?.slice(0, 10) || '—'} →{' '}
                  {manifestData.scope?.to?.slice(0, 10) || '—'}
                </dd>
              </div>
              <div>
                <dt>{t('NLSVerifyEvidenceGenerated')}</dt>
                <dd>
                  {manifestData.generatedAt
                    ? new Date(manifestData.generatedAt).toLocaleString()
                    : '—'}
                </dd>
              </div>
              <div>
                <dt>{t('NLSVerifyEvidenceKeyId')}</dt>
                <dd className="mono">{manifestData.keyVersion || '—'}</dd>
              </div>
            </dl>

            {manifestData.files && manifestData.files.length > 0 && (
              <div className="verifyFilesSection">
                <h3>{t('NLSVerifyEvidenceFiles')}</h3>
                <div className="evidenceTableWrapper">
                  <table className="tb">
                    <thead>
                      <tr>
                        <th>{t('NLSVerifyEvidenceTablePart')}</th>
                        <th>{t('NLSVerifyEvidenceTablePath')}</th>
                        <th>{t('NLSVerifyEvidenceTableSha256')}</th>
                        <th>{t('NLSVerifyEvidenceTableSize')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {manifestData.files.map((file) => (
                        <tr key={file.path}>
                          <td>{file.part}</td>
                          <td>
                            <strong>{file.path}</strong>
                          </td>
                          <td className="mono">{file.sha256}</td>
                          <td>{formatFileSize(file.bytes)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="verifyOfflineSection">
                  <h4>{t('NLSVerifyEvidenceChecksumTitle')}</h4>
                  <p className="field__hint">{t('NLSVerifyEvidenceChecksumHelp')}</p>
                  <div className="verifyCommandBox">
                    <Button variant="secondary" size="sm" onClick={handleDownloadSums}>
                      {t('NLSVerifyEvidenceDownloadChecksums')}
                    </Button>
                    <pre className="mono">{FIXED_SHASUM_COMMAND}</pre>
                    <Button variant="secondary" size="sm" onClick={handleCopyFixedCommand}>
                      {copied ? t('NLSVerifyEvidenceCopied') : t('NLSVerifyEvidenceCopyOffline')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  )
}
